import React from 'react';
import { debounce } from 'lodash';
import { useSWRConfig } from 'swr';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { useNavigate, useParams } from 'react-router-dom';
import { exportIcon, eyeIcon } from '@progress/kendo-svg-icons';

import {
  NotificationContent,
  NotificationContext
} from '../../../contexts/notification';
import { useReels } from '../../../contexts/reels';
import { GameContext } from '../../../contexts/game';
import { useSaveReels } from '../../../hooks/use-reels';
import { GameDocumentContext } from '../../../contexts/game-document';
import { ProcessVideo } from '../../../components/dialog/reels/process-video';

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const { gameCode } = useParams();
  const { mutate } = useSWRConfig();

  const [game] = React.useContext(GameContext);
  const [gameDocument] = React.useContext(GameDocumentContext);
  const [, setNotification] = React.useContext(NotificationContext);

  const [dialog, setDialog] = React.useState('');

  const {
    isDirty,
    timeline,
    resources,
    textSlides,
    global_settings,
    versionIdentifier,
    handleChange
  } = useReels();
  const { trigger: triggerSave } = useSaveReels(gameCode ?? '');

  const notificationHandler = (message: string) => {
    const reelsNotification: NotificationContent = {
      icon: 'notifications',
      isHide: false,
      message: (
        <span>
          <strong>{message}</strong>
        </span>
      )
    };
    setNotification((prevNotification) => ({
      ...prevNotification,
      content: [...prevNotification.content, reelsNotification]
    }));
  };

  const onSave = React.useCallback(async () => {
    const projectContent = {
      project_metadata: {
        gameCode
      },
      timeline,
      resources,
      textSlides,
      global_settings
    };

    const payload = {
      projectContent: JSON.stringify(projectContent),
      totalDuration: timeline
        .map((t) => t.duration_secs)
        .reduce((current, next) => current + next, 0),
      versionIdentifier
    };

    try {
      const newData = await triggerSave(payload);
      handleChange({
        isDirty: false,
        versionIdentifier: newData.versionIdentifier
      });
      notificationHandler('Reels draft saved');
    } catch (error) {
      notificationHandler(
        'Failed to save reels draft. Restore to previous version.'
      );
      // when draft failed to save, should restore the state to previous last saved draft
      mutate(`games/${gameCode}/reels-projects`);
    }
  }, [
    gameCode,
    timeline,
    resources,
    textSlides,
    global_settings,
    versionIdentifier,
    triggerSave
  ]);

  const debouncedSave = React.useMemo(() => debounce(onSave, 500), [onSave]);

  React.useEffect(() => {
    if (isDirty) debouncedSave();
  }, [isDirty, debouncedSave]);

  React.useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  return (
    <>
      <div
        className={'d-flex align-items-center justify-content-between'}
        style={{
          padding: '24px 16px'
        }}>
        <div
          className={'d-flex align-items-center'}
          style={{
            gap: '16px'
          }}>
          <span
            className={'material-symbols-outlined cursor-pointer'}
            onClick={() => navigate(`/games/${gameCode}/facilitator`)}
            style={{
              fontSize: '24px'
            }}>
            arrow_back
          </span>
          <div className={'d-flex flex-column gap-1 justify-content-center'}>
            <span>
              {game.activityState?.eventName}
              {gameDocument.gameDocument &&
                ` - ${gameDocument.gameDocument.name}`}
            </span>
            <h1 className={'text-primary'}>
              <b>Re-cap reel</b>
            </h1>
          </div>
        </div>

        <div className={'d-flex gap-2'}>
          <Button
            themeColor={'dark'}
            fillMode={'outline'}
            onClick={() => setDialog('preview')}
            startIcon={<SvgIcon icon={eyeIcon} />}>
            Preview
          </Button>
          <Button
            themeColor={'primary'}
            onClick={() => setDialog('export')}
            startIcon={<SvgIcon icon={exportIcon} />}>
            Export
          </Button>
        </div>
      </div>

      {dialog && (
        <ProcessVideo
          eventName={game.activityState?.eventName ?? `highlight-${gameCode}`}
          dialog={dialog}
          onClose={() => setDialog('')}
        />
      )}
    </>
  );
};
